import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { Icon } from '@/deploy/components/ui/Icon';
import { Button } from '@/deploy/components/ui/Button';
import { Badge } from '@/deploy/components/ui/Badge';
import { useSessionStore } from '@/deploy/stores/session.store';
import { useContainersStore } from '@/deploy/stores/containers.store';
import {
  canRetryRelease,
  containerReleasesService,
  type ContainerRelease,
  type ReleaseStatus,
} from '@/deploy/services/container-releases.service';
import {
  RetryError,
  RetryNotes,
  RetryReleaseButton,
  useRetryRelease,
  type RetryState,
} from '@/deploy/components/container/RetryRelease';
import { formatRelative } from '@/deploy/utils/format';
import styles from './DeployProgress.module.scss';

// ============================================================
// Page /deploy/progress — Suivi en temps réel des releases
// ============================================================
// Pour chaque container du projet, on poll l'active release + la dernière
// terminée. Auto-refresh toutes les 5s tant qu'au moins une release est
// active. On masque les containers sans aucune release.
// ============================================================

const POLL_INTERVAL_MS = 5_000;
const HISTORY_PAGE_SIZE = 25;
/** pageSize maximum accepté par le CICD pour la liste projet. */
const HISTORY_MAX_PAGE_SIZE = 200;

// Étapes "user-facing" d'une release. Une release passe par ces 4 étapes.
type StepKey = 'update' | 'curation' | 'merge' | 'image';
type StepState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

interface StepDescriptor {
  key: StepKey;
  label: string;
  icon: 'zap' | 'check' | 'git-commit' | 'rocket';
}

const STEPS: StepDescriptor[] = [
  { key: 'update',   label: 'CI update.yml',  icon: 'zap' },
  { key: 'curation', label: 'Curation + PR',  icon: 'git-commit' },
  { key: 'merge',    label: 'Merge → main',   icon: 'check' },
  { key: 'image',    label: 'Image Docker',   icon: 'rocket' },
];

function stepsFor(release: ContainerRelease): Record<StepKey, StepState> {
  const s: Record<StepKey, StepState> = {
    update: 'pending', curation: 'pending', merge: 'pending', image: 'pending',
  };
  switch (release.status) {
    case 'update-pending':
      s.update = 'running'; break;
    case 'update-failed':
      s.update = 'failed'; s.curation = 'skipped'; s.merge = 'skipped'; s.image = 'skipped'; break;
    case 'review-required':
      s.update = 'done'; s.curation = 'running'; break;
    case 'applying':
      s.update = 'done'; s.curation = 'running'; break;
    case 'applied':
      s.update = 'done'; s.curation = 'done'; s.merge = 'running'; break;
    case 'pr-merged':
      s.update = 'done'; s.curation = 'done'; s.merge = 'done'; s.image = 'running'; break;
    case 'released':
      s.update = 'done'; s.curation = 'done'; s.merge = 'done'; s.image = 'done'; break;
    case 'cancelled':
      s.update = 'done'; s.curation = 'skipped'; s.merge = 'skipped'; s.image = 'skipped'; break;
    case 'failed':
      // À partir de pr-merged en général
      s.update = 'done'; s.curation = 'done'; s.merge = 'done'; s.image = 'failed'; break;
  }
  return s;
}

const STATUS_LABEL: Record<ReleaseStatus, string> = {
  'update-pending':   'CI update.yml en cours',
  'update-failed':    'CI update.yml échouée',
  'review-required':  'Review manuelle requise',
  'applying':         'Application de la curation…',
  'applied':          'Curation appliquée, PR ouverte',
  'pr-merged':        'PR mergée, build de l\'image…',
  'released':         'Release publiée ✓',
  'cancelled':        'Annulée',
  'failed':           'Échec',
};

const STATUS_TONE: Record<ReleaseStatus, 'info' | 'warning' | 'success' | 'danger' | 'muted'> = {
  'update-pending':  'info',
  'update-failed':   'danger',
  'review-required': 'warning',
  'applying':        'info',
  'applied':         'info',
  'pr-merged':       'info',
  'released':        'success',
  'cancelled':       'muted',
  'failed':          'danger',
};

function isActiveStatus(status: ReleaseStatus): boolean {
  return !['released', 'cancelled', 'failed', 'update-failed'].includes(status);
}

interface PerContainer {
  active: ContainerRelease | null;
  loading: boolean;
}

export function DeployProgress() {
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());
  const containers = useContainersStore((s) => s.containers);
  const loadedProjectId = useContainersStore((s) => s.loadedProjectId);
  const load = useContainersStore((s) => s.load);

  const [states, setStates] = useState<Record<string, PerContainer>>({});
  const [tab, setTab] = useState<'active' | 'history'>('active');
  // Historique = liste projet paginée (et non plus la dernière release de chaque container).
  const [history, setHistory] = useState<ContainerRelease[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailOf, setDetailOf] = useState<string | null>(null);
  // Ref → on lit la dernière valeur sans relancer le setInterval
  const statesRef = useRef(states);
  statesRef.current = states;
  const historyPageRef = useRef(historyPage);
  historyPageRef.current = historyPage;

  const projectContainers = useMemo(
    () => containers.filter((c) => c.projectId === project.id),
    [containers, project.id],
  );

  useEffect(() => {
    if (!project.id) return;
    void load(project.id);
  }, [project.id, load]);

  // Purge l'état au changement de projet : sinon des entrées « actives » fantômes de l'ancien
  // projet font tourner le poller à l'infini (anyActive reste vrai) et peuvent flasher la
  // mauvaise release si un code de container est réutilisé entre projets.
  useEffect(() => {
    setStates({}); setTab('active');
    setHistory([]); setHistoryTotal(0); setHistoryPage(0); setDetailOf(null);
  }, [project.id]);

  const loadHistory = useCallback(async (page: number, append: boolean) => {
    if (!project.id) return;
    setHistoryLoading(true);
    try {
      const res = await containerReleasesService.listByProject(project.id, { page, pageSize: HISTORY_PAGE_SIZE });
      setHistory((prev) => (append ? [...prev, ...res.items] : res.items));
      setHistoryTotal(res.total);
      setHistoryPage(page);
    } catch {
      // best-effort : la liste reste ce qu'elle est
    } finally {
      setHistoryLoading(false);
    }
  }, [project.id]);

  // Recharge d'un coup les pages déjà affichées (409 sur une relance, bouton Rafraîchir), sans
  // revenir à la page 0 : les lignes chargées par « Charger plus » restent. Au-delà du pageSize
  // maximum du CICD, on garde les premières pages et « Charger plus » repart de la suivante.
  const reloadHistory = useCallback(async () => {
    if (!project.id) return;
    const pages = Math.min(historyPageRef.current + 1, Math.floor(HISTORY_MAX_PAGE_SIZE / HISTORY_PAGE_SIZE));
    setHistoryLoading(true);
    try {
      const res = await containerReleasesService.listByProject(project.id, { page: 0, pageSize: pages * HISTORY_PAGE_SIZE });
      setHistory(res.items);
      setHistoryTotal(res.total);
      setHistoryPage(pages - 1);
    } catch {
      // best-effort : la liste reste ce qu'elle est
    } finally {
      setHistoryLoading(false);
    }
  }, [project.id]);

  // Première page chargée à l'ouverture de l'onglet (et rechargée au changement de projet).
  useEffect(() => {
    if (tab !== 'history' || history.length > 0) return;
    void loadHistory(0, false);
  }, [tab, history.length, loadHistory]);

  const refreshContainer = useCallback(async (code: string) => {
    try {
      // Seule la release active est nécessaire ici : l'historique vient de la liste projet paginée,
      // plus de `list(code, 5)` par container à chaque tick de polling.
      const prev = statesRef.current[code]?.active ?? null;
      const active = await containerReleasesService.active(project.id, code);
      setStates((p) => ({ ...p, [code]: { active, loading: false } }));
      // L'historique suit : la ligne de la release active, et celle qui vient de finir (relancée
      // puis publiée ou de nouveau en échec), sans quoi elle resterait figée « en cours »
      if (active) setHistory((h) => h.map((r) => (r.id === active.id ? active : r)));
      if (prev && prev.id !== active?.id) {
        const done = await containerReleasesService.get(project.id, code, prev.id).catch(() => null);
        if (done) setHistory((h) => h.map((r) => (r.id === done.id ? done : r)));
      }
    } catch {
      // best-effort
    }
  }, [project.id]);

  // Relance d'une release en échec : la ligne d'historique prend le nouvel état et le container
  // repasse dans « En cours » (le polling reprend tant qu'il y a une release active).
  const onRetried = useCallback(async (updated: ContainerRelease) => {
    setHistory((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    await refreshContainer(updated.containerCode);
  }, [refreshContainer]);

  // Relance refusée (409) : la vue n'était plus à jour, on recharge le container et l'historique
  const onRetryConflict = useCallback((code: string) =>
    Promise.all([refreshContainer(code), reloadHistory()]), [refreshContainer, reloadHistory]);

  // Même règle que le CICD : rien d'autre en cours sur le container, pas de version plus récente publiée
  const retryableOf = useCallback((r: ContainerRelease) =>
    canRetryRelease(r, [...history, states[r.containerCode]?.active]), [history, states]);

  // Initial load
  useEffect(() => {
    if (loadedProjectId !== project.id) return;
    projectContainers.forEach((c) => { void refreshContainer(c.code); });
  }, [loadedProjectId, project.id, projectContainers, refreshContainer]);

  // Polling: ne tourne QUE si au moins une release est active
  useEffect(() => {
    if (loadedProjectId !== project.id) return;
    const t = setInterval(() => {
      const anyActive = Object.values(statesRef.current).some((s) => s.active && isActiveStatus(s.active.status));
      if (!anyActive) return;
      projectContainers.forEach((c) => { void refreshContainer(c.code); });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [loadedProjectId, project.id, projectContainers, refreshContainer]);

  // Split En cours / Historique : actif = release active en cours ; historique = liste projet.
  const activeContainers = projectContainers.filter((c) => {
    const a = states[c.code]?.active;
    return a && isActiveStatus(a.status);
  });
  const activeCount = activeContainers.length;
  const doneCount = history.filter((r) => r.status === 'released').length;
  const failedCount = history.filter((r) => ['failed', 'update-failed', 'cancelled'].includes(r.status)).length;
  const containerNameOf = (code: string) =>
    projectContainers.find((c) => c.code === code)?.displayName ?? code;
  const containerColorOf = (code: string) =>
    projectContainers.find((c) => c.code === code)?.color ?? '#8B5CF6';
  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Suivi des releases"
        title="Releases en cours"
        subtitle="Suivi en temps réel des releases lancées depuis le panneau. Auto-refresh toutes les 5s tant qu'il y a une release active."
        actions={
          <div className={styles.headerActions}>
            <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />}
              onClick={() => {
                projectContainers.forEach((c) => { void refreshContainer(c.code); });
                if (history.length > 0) void reloadHistory();
              }}>
              Rafraîchir
            </Button>
            <Button variant="primary" size="sm" leftIcon={<Icon name="rocket" size={12} />}
              onClick={() => navigate('/deploiement/deploy')}>
              Nouveau déploiement
            </Button>
          </div>
        }
      />

      <section className={styles.summary}>
        <div className={styles.summaryItem}>
          <span className={[styles.num, activeCount > 0 && styles.numHl].filter(Boolean).join(' ')}>{activeCount}</span>
          <em>en cours</em>
        </div>
        <div className={styles.summaryItem}>
          <span className={[styles.num, doneCount > 0 && styles.numOk].filter(Boolean).join(' ')}>{doneCount}</span>
          <em>publiées</em>
        </div>
        <div className={styles.summaryItem}>
          <span className={[styles.num, failedCount > 0 && styles.numKo].filter(Boolean).join(' ')}>{failedCount}</span>
          <em>échouées / annulées</em>
        </div>
      </section>

      {/* Mini-navigation : En cours / Historique */}
      <nav className={styles.tabs}>
        <button type="button"
          className={[styles.tab, tab === 'active' && styles.tabActive].filter(Boolean).join(' ')}
          onClick={() => setTab('active')}>
          <Icon name="refresh" size={13} /> <span>En cours</span> <span className={styles.count}>{activeContainers.length}</span>
        </button>
        <button type="button"
          className={[styles.tab, tab === 'history' && styles.tabActive].filter(Boolean).join(' ')}
          onClick={() => setTab('history')}>
          <Icon name="history" size={13} /> <span>Historique</span> <span className={styles.count}>{historyTotal}</span>
        </button>
      </nav>

      {tab === 'active' ? (
        activeContainers.length === 0 ? (
          <div className={styles.empty}>
            <Icon name="rocket" size={32} />
            <p>Aucune release en cours.</p>
            <Button variant="primary" leftIcon={<Icon name="plus" size={12} />} onClick={() => navigate('/deploiement/deploy')}>
              Lancer un déploiement
            </Button>
          </div>
        ) : (
          <div className={styles.cardList}>
            {activeContainers.map((c) => (
              <ReleaseCard
                key={c.id}
                containerCode={c.code}
                containerName={c.displayName}
                containerColor={c.color}
                release={states[c.code]!.active!}
                isActive
                onOpen={() => navigate(`/deploiement/containers/${c.code}`)}
              />
            ))}
          </div>
        )
      ) : (
        <HistoryList
          releases={history}
          total={historyTotal}
          loading={historyLoading}
          detailOf={detailOf}
          onToggleDetail={(id) => setDetailOf((prev) => (prev === id ? null : id))}
          onMore={() => void loadHistory(historyPage + 1, true)}
          nameOf={containerNameOf}
          colorOf={containerColorOf}
          onOpen={(code) => navigate(`/deploiement/containers/${code}`)}
          projectId={project.id}
          retryableOf={retryableOf}
          onRetried={onRetried}
          onRetryConflict={onRetryConflict}
        />
      )}
    </div>
  );
}

// ============================================================
// Historique : la LISTE (qui / quand / quoi), le détail au clic
// ============================================================
// Une ligne par release, du plus récent au plus ancien, toutes containers confondues. La frise des
// 4 étapes reste disponible mais à la demande : ce qu'on veut lire d'abord, c'est qui a lancé quoi.
function HistoryList({
  releases, total, loading, detailOf, onToggleDetail, onMore, nameOf, colorOf, onOpen,
  projectId, retryableOf, onRetried, onRetryConflict,
}: {
  releases: ContainerRelease[];
  total: number;
  loading: boolean;
  detailOf: string | null;
  onToggleDetail: (id: string) => void;
  onMore: () => void;
  nameOf: (code: string) => string;
  colorOf: (code: string) => string;
  onOpen: (code: string) => void;
  projectId: string;
  retryableOf: (r: ContainerRelease) => boolean;
  onRetried: (updated: ContainerRelease) => Promise<void>;
  onRetryConflict: (code: string) => Promise<unknown>;
}) {
  if (releases.length === 0) {
    return (
      <div className={styles.empty}>
        <Icon name="history" size={32} />
        <p>{loading ? 'Chargement de l\'historique…' : 'Aucune release enregistrée sur ce projet.'}</p>
      </div>
    );
  }
  return (
    <>
      <div className={styles.histTable}>
        <div className={[styles.histRow, styles.histRowRetry, styles.histHead].join(' ')}>
          <span>Quoi</span>
          <span>Version</span>
          <span>Qui</span>
          <span>Quand</span>
          <span>Statut</span>
          <span />
        </div>
        {releases.map((r) => (
          <HistoryItem
            key={r.id}
            release={r}
            open={detailOf === r.id}
            onToggleDetail={() => onToggleDetail(r.id)}
            name={nameOf(r.containerCode)}
            color={colorOf(r.containerCode)}
            onOpen={() => onOpen(r.containerCode)}
            projectId={projectId}
            retryable={retryableOf(r)}
            onRetried={onRetried}
            onRetryConflict={onRetryConflict}
          />
        ))}
      </div>
      {releases.length < total && (
        <div className={styles.histMore}>
          <Button variant="ghost" size="sm" onClick={onMore} loading={loading}>
            Charger plus ({releases.length} / {total})
          </Button>
        </div>
      )}
    </>
  );
}

// Une ligne d'historique : le bouton « Réessayer » d'une release en échec est sur la ligne, ou
// dans le détail quand il est ouvert (même état de relance, pas de double bouton).
function HistoryItem({
  release: r, open, onToggleDetail, name, color, onOpen, projectId, retryable, onRetried, onRetryConflict,
}: {
  release: ContainerRelease;
  open: boolean;
  onToggleDetail: () => void;
  name: string;
  color: string;
  onOpen: () => void;
  projectId: string;
  retryable: boolean;
  onRetried: (updated: ContainerRelease) => Promise<void>;
  onRetryConflict: (code: string) => Promise<unknown>;
}) {
  const onConflict = useCallback(() => onRetryConflict(r.containerCode), [onRetryConflict, r.containerCode]);
  const retry = useRetryRelease(projectId, r, retryable, onRetried, onConflict);
  return (
    <div className={styles.histGroup}>
      <div className={[styles.histRow, styles.histRowRetry].join(' ')}>
        <span className={styles.histWhat}>
          <i className={styles.histDot} style={{ background: color }} />
          <button type="button" className={styles.histLink} onClick={onOpen}>
            {name}
          </button>
        </span>
        <code className={styles.histVersion}>{r.branchName}</code>
        <span className={styles.histWho}>{r.initiatedByName || r.initiatedBy || 'inconnu'}</span>
        <span className={styles.histWhen} title={new Date(r.initiatedAt).toLocaleString('fr-FR')}>
          {formatRelative(r.initiatedAt)}
        </span>
        <span className={styles.histStatus}>
          <Badge tone={STATUS_TONE[r.status] ?? 'muted'}>{STATUS_LABEL[r.status] ?? r.status}</Badge>
          <RetryNotes release={r} />
        </span>
        <span className={styles.histActions}>
          {retryable && !open && <RetryReleaseButton state={retry} />}
          <Button variant="ghost" size="sm" onClick={onToggleDetail}>
            {open ? 'Masquer' : 'Détail'}
          </Button>
        </span>
      </div>
      {retryable && retry.error && (
        <div className={styles.histRetryError}><RetryError state={retry} /></div>
      )}
      {open && (
        <div className={styles.histDetail}>
          <ReleaseCard
            containerCode={r.containerCode}
            containerName={name}
            containerColor={color}
            release={r}
            isActive={false}
            onOpen={onOpen}
            retry={retryable ? retry : undefined}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Dernière erreur de la release, si elle date d'après la dernière relance (manuelle ou
 * automatique) : `errors` ne fait que s'allonger, l'échec d'avant la relance ne doit pas s'afficher
 * sur le run relancé. Pas de filtre sur le statut, pour garder les avertissements posés pendant une
 * release en cours ou publiée (auto-merge, auto-apply, image Docker KO).
 */
function lastErrorSinceRetry(release: ContainerRelease) {
  const last = release.errors && release.errors.length > 0 ? release.errors[release.errors.length - 1] : null;
  if (!last) return null;
  const retriedAt = Math.max(
    release.lastRetryAt ? new Date(release.lastRetryAt).getTime() : 0,
    release.autoRetriedAt ? new Date(release.autoRetriedAt).getTime() : 0,
  );
  return !retriedAt || new Date(last.at).getTime() > retriedAt ? last : null;
}

// ============================================================
// Card 1 release
// ============================================================
interface ReleaseCardProps {
  containerCode: string;
  containerName: string;
  containerColor: string;
  release: ContainerRelease;
  isActive: boolean;
  onOpen: () => void;
  /** Release en échec relançable : bouton « Réessayer » dans l'en-tête. */
  retry?: RetryState;
}

function ReleaseCard({ containerName, containerColor, release, isActive, onOpen, retry }: ReleaseCardProps) {
  const steps = stepsFor(release);
  const lastError = lastErrorSinceRetry(release);
  const startedAt = release.initiatedAt;
  const updatedAt = release.updatedAt ?? release.initiatedAt;

  return (
    <article className={[
      styles.card,
      release.status === 'released' && styles.cardOk,
      ['failed', 'update-failed', 'cancelled'].includes(release.status) && styles.cardKo,
      isActive && styles.cardActive,
    ].filter(Boolean).join(' ')}>
      <header className={styles.cardHeader}>
        <span className={styles.chip} style={{ background: containerColor }}>
          <Icon name="package" size={14} />
        </span>
        <div className={styles.cardTitle}>
          <strong>{containerName}</strong>
          <code className={styles.branch}>{release.branchName}</code>
        </div>
        <Badge tone={STATUS_TONE[release.status]}>
          {isActive && <span className={styles.pulse} />}
          {STATUS_LABEL[release.status]}
        </Badge>
        <RetryNotes release={release} />
        {retry && <RetryReleaseButton state={retry} />}
        <Button variant="ghost" size="sm" leftIcon={<Icon name="external" size={11} />} onClick={onOpen}>
          Détail
        </Button>
      </header>

      <div className={styles.timeline}>
        {STEPS.map((step, idx) => {
          const state = steps[step.key];
          return (
            <div key={step.key} className={styles.stepWrap}>
              <div className={[
                styles.step,
                styles[`state_${state}`],
              ].join(' ')}>
                {state === 'done' ? <Icon name="check" size={14} />
                 : state === 'running' ? <span className={styles.spinner} />
                 : state === 'failed' ? <Icon name="warn" size={14} />
                 : <Icon name={step.icon} size={12} />}
              </div>
              <div className={styles.stepLabel}>
                <span>{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && <div className={[styles.connector, styles[`conn_${state}`]].join(' ')} />}
            </div>
          );
        })}
      </div>

      {lastError && (
        <div className={styles.errBox}>
          <Icon name="warn" size={12} />
          <span><strong>{lastError.phase}</strong>: {lastError.message}</span>
        </div>
      )}

      <footer className={styles.cardFooter}>
        <span>Démarré {formatRelative(startedAt)}</span>
        {updatedAt !== startedAt && <span>· MAJ {formatRelative(updatedAt)}</span>}
        {release.pullRequestUrl && (
          <a href={release.pullRequestUrl} target="_blank" rel="noreferrer" className={styles.link}>
            <Icon name="external" size={11} /> PR #{release.pullRequestNumber}
          </a>
        )}
        {release.githubReleaseUrl && (
          <a href={release.githubReleaseUrl} target="_blank" rel="noreferrer" className={styles.link}>
            <Icon name="external" size={11} /> Release GitHub
          </a>
        )}
        {release.dockerImage && (
          <code className={styles.image}>{release.dockerImage}</code>
        )}
      </footer>
    </article>
  );
}
